import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Button } from "./ui/button";
import {
  Gauge,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  FileWarning,
  ChevronDown,
  ChevronUp,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  XCircle,
  Upload,
  FileText,
} from "lucide-react";

const strengthColors = {
  strong: "bg-green-100 text-green-700 border-green-200",
  adequate: "bg-blue-100 text-blue-700 border-blue-200",
  good: "bg-blue-100 text-blue-700 border-blue-200",
  partial: "bg-amber-100 text-amber-700 border-amber-200",
  weak: "bg-red-100 text-red-700 border-red-200",
  no_evidence: "bg-red-100 text-red-700 border-red-200",
};

const strengthLabels = {
  strong: "Strong",
  adequate: "Adequate",
  good: "Good",
  partial: "Partial",
  weak: "Weak",
  no_evidence: "No Evidence",
};

// === Confidence Calibration Panel ===
export function ConfidencePanel({ confidenceData, aiConfidence }) {
  if (!confidenceData) return null;

  const { calculated, evidence_breakdown, total_competencies } = confidenceData;
  const aiReported = aiConfidence || confidenceData.ai_reported || 0;
  const diff = Math.round(calculated - aiReported);

  return (
    <Card className="border-slate-200" data-testid="confidence-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
          <Gauge className="w-4 h-4" />
          Confidence Calibration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Dual confidence bars */}
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-500">AI Self-Reported</span>
              <span className="text-sm font-semibold text-slate-700">{aiReported}%</span>
            </div>
            <Progress value={aiReported} className="h-2" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-500">Evidence-Based (Calculated)</span>
              <span className="text-sm font-bold text-blue-700">{calculated}%</span>
            </div>
            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  calculated >= 80 ? "bg-green-500" : calculated >= 60 ? "bg-blue-500" : calculated >= 40 ? "bg-amber-500" : "bg-red-500"
                }`}
                style={{ width: `${calculated}%` }}
              />
            </div>
          </div>
        </div>

        {/* Variance indicator */}
        {diff !== 0 && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${
            Math.abs(diff) <= 10 ? "bg-green-50 text-green-700" : Math.abs(diff) <= 25 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"
          }`} data-testid="confidence-variance">
            {diff > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
            {Math.abs(diff)}% {diff > 0 ? "above" : "below"} AI self-report
            {Math.abs(diff) > 25 && " — significant variance"}
          </div>
        )}

        {/* Evidence strength breakdown */}
        {evidence_breakdown && (
          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs text-slate-500 mb-2">Evidence Strength Distribution</p>
            <div className="flex gap-1.5 flex-wrap">
              {[
                { key: "strong", color: "bg-green-500" },
                { key: "adequate", color: "bg-blue-500" },
                { key: "partial", color: "bg-amber-500" },
                { key: "weak", color: "bg-red-400" },
                { key: "no_evidence", color: "bg-red-600" },
              ].map(({ key, color }) => {
                const count = evidence_breakdown[key] || 0;
                if (count === 0) return null;
                return (
                  <div key={key} className="flex items-center gap-1 text-xs">
                    <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
                    <span className="text-slate-600 capitalize">{key.replace("_", " ")}: {count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// === Gap Report Panel ===
export function GapReportPanel({ gapReport, isCandidate = false }) {
  const [expanded, setExpanded] = useState(false);

  if (!gapReport || !gapReport.gaps || gapReport.gaps.length === 0) return null;

  const displayGaps = expanded ? gapReport.gaps : gapReport.gaps.slice(0, 3);

  return (
    <Card className="border-amber-200 bg-amber-50/30" data-testid="gap-report-panel">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base font-semibold flex items-center gap-2 text-amber-800">
              <FileWarning className="w-4 h-4" />
              Evidence Gap Report
            </CardTitle>
            <CardDescription className="text-amber-600 mt-1">
              {gapReport.gaps_count} of {gapReport.total_competencies} competencies need stronger evidence
            </CardDescription>
          </div>
          <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300" data-testid="gap-count-badge">
            {gapReport.gaps_count} gaps
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {displayGaps.map((gap) => (
          <div
            key={gap.competency_id}
            className="p-3 bg-white rounded-lg border border-amber-200"
            data-testid={`gap-item-${gap.competency_id}`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <span className="text-xs font-mono text-slate-400">{gap.unit_code}</span>
                <p className="text-sm font-medium text-slate-800">{gap.title}</p>
              </div>
              <Badge className={`text-[10px] px-2 py-0.5 ${strengthColors[gap.evidence_strength] || strengthColors.no_evidence}`}>
                {strengthLabels[gap.evidence_strength] || "No Evidence"}
              </Badge>
            </div>

            {/* AI Finding */}
            <p className="text-xs text-slate-600 mb-2">
              <span className="font-medium text-slate-500">AI Finding: </span>
              {gap.ai_finding}
            </p>

            {/* What to upload */}
            {gap.suggested_documents && gap.suggested_documents.length > 0 && (
              <div className="mt-2 pt-2 border-t border-amber-100">
                <p className="text-xs font-medium text-amber-700 mb-1 flex items-center gap-1">
                  <Upload className="w-3 h-3" />
                  {isCandidate ? "Upload to strengthen:" : "Candidate should provide:"}
                </p>
                <ul className="text-xs text-amber-600 space-y-0.5 ml-4 list-disc">
                  {gap.suggested_documents.map((doc, i) => (
                    <li key={i}>{doc}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}

        {gapReport.gaps.length > 3 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-amber-700 hover:bg-amber-100"
            onClick={() => setExpanded(!expanded)}
            data-testid="gap-toggle-expand"
          >
            {expanded ? (
              <>Show Less <ChevronUp className="w-4 h-4 ml-1" /></>
            ) : (
              <>Show All {gapReport.gaps.length} Gaps <ChevronDown className="w-4 h-4 ml-1" /></>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// === Analysis Comparison Panel ===
export function AnalysisComparisonPanel({ comparison }) {
  if (!comparison) return null;

  const hasImprovements = comparison.improvements?.length > 0;
  const hasRegressions = comparison.regressions?.length > 0;
  const coverageUp = comparison.coverage_change > 0;
  const confidenceUp = comparison.confidence_change > 0;

  return (
    <Card className="border-blue-200 bg-blue-50/30" data-testid="analysis-comparison-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2 text-blue-800">
          <TrendingUp className="w-4 h-4" />
          Re-Analysis Comparison
          <Badge variant="outline" className="ml-auto text-xs bg-blue-100 text-blue-700 border-blue-300">
            Run #{comparison.current_run} vs #{comparison.previous_run}
          </Badge>
        </CardTitle>
        {comparison.docs_added > 0 && (
          <CardDescription className="text-blue-600">
            {comparison.docs_added} new document{comparison.docs_added > 1 ? "s" : ""} added since last analysis
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Key metrics */}
        <div className="grid grid-cols-2 gap-3">
          <div className={`p-3 rounded-lg border ${coverageUp ? "bg-green-50 border-green-200" : comparison.coverage_change < 0 ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`}>
            <p className="text-xs text-slate-500">Evidence Coverage</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-lg font-bold text-slate-900">{comparison.current_coverage}%</span>
              {comparison.coverage_change !== 0 && (
                <span className={`text-xs font-medium flex items-center gap-0.5 ${coverageUp ? "text-green-600" : "text-red-600"}`} data-testid="coverage-change">
                  {coverageUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {coverageUp ? "+" : ""}{comparison.coverage_change}%
                </span>
              )}
            </div>
            <p className="text-[10px] text-slate-400">was {comparison.previous_coverage}%</p>
          </div>
          <div className={`p-3 rounded-lg border ${confidenceUp ? "bg-green-50 border-green-200" : comparison.confidence_change < 0 ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`}>
            <p className="text-xs text-slate-500">Calculated Confidence</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-lg font-bold text-slate-900">{comparison.current_confidence}%</span>
              {comparison.confidence_change !== 0 && (
                <span className={`text-xs font-medium flex items-center gap-0.5 ${confidenceUp ? "text-green-600" : "text-red-600"}`} data-testid="confidence-change">
                  {confidenceUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {confidenceUp ? "+" : ""}{comparison.confidence_change}%
                </span>
              )}
            </div>
            <p className="text-[10px] text-slate-400">was {comparison.previous_confidence}%</p>
          </div>
        </div>

        {/* Improvements */}
        {hasImprovements && (
          <div data-testid="comparison-improvements">
            <p className="text-xs font-medium text-green-700 mb-2 flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5" />
              Improved ({comparison.improvements.length})
            </p>
            <div className="space-y-1.5">
              {comparison.improvements.map((imp) => (
                <div key={imp.competency_id} className="flex items-center gap-2 text-xs bg-green-50 px-3 py-1.5 rounded-md">
                  <span className="text-slate-600 truncate flex-1">{imp.unit_code} — {imp.title}</span>
                  <span className="text-red-400 line-through">{imp.old_strength}</span>
                  <ArrowUpRight className="w-3 h-3 text-green-500" />
                  <span className="text-green-700 font-medium">{imp.new_strength}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Regressions */}
        {hasRegressions && (
          <div data-testid="comparison-regressions">
            <p className="text-xs font-medium text-red-700 mb-2 flex items-center gap-1">
              <XCircle className="w-3.5 h-3.5" />
              Regressed ({comparison.regressions.length})
            </p>
            <div className="space-y-1.5">
              {comparison.regressions.map((reg) => (
                <div key={reg.competency_id} className="flex items-center gap-2 text-xs bg-red-50 px-3 py-1.5 rounded-md">
                  <span className="text-slate-600 truncate flex-1">{reg.unit_code} — {reg.title}</span>
                  <span className="text-green-500">{reg.old_strength}</span>
                  <ArrowDownRight className="w-3 h-3 text-red-500" />
                  <span className="text-red-700 font-medium">{reg.new_strength}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary line */}
        {!hasImprovements && !hasRegressions && (
          <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
            <Minus className="w-3.5 h-3.5" />
            No changes in individual competency evidence strengths
          </div>
        )}
      </CardContent>
    </Card>
  );
}
